import type { Transaction } from '@codemirror/state'
import { EditorView } from '@codemirror/view'
import { classifyInsertion, type OpKind, type RecordInput, type Recorder } from '@coderaw/log'

export type RawChange = { readonly fromA: number; readonly toA: number; readonly text: string }

/**
 * CodeMirror reports every change in a transaction against the ORIGINAL
 * document. The log wants ops that apply one after another, so each op's
 * range is shifted by the net length change of the ops before it.
 */
export const sequentialOps = (changes: readonly RawChange[]): readonly RecordInput[] => {
  let shift = 0
  return changes.map((change) => {
    const op = { from: change.fromA + shift, to: change.toA + shift, text: change.text }
    shift += change.text.length - (change.toA - change.fromA)
    return op
  })
}

export const collectChanges = (tr: Transaction): readonly RawChange[] => {
  const out: RawChange[] = []
  tr.changes.iterChanges((fromA, toA, _fromB, _toB, inserted) => {
    out.push({ fromA, toA, text: inserted.toString() })
  })
  return out
}

/** Undo and redo are recorded as their own kinds so replays can show them. */
export const kindForTransaction = (tr: Transaction): OpKind | undefined => {
  if (tr.isUserEvent('undo')) return 'undo'
  if (tr.isUserEvent('redo')) return 'redo'
  return undefined
}

/**
 * One keystroke can arrive as a multi-character change when CodeMirror folds
 * auto-indent into it (typing `}` on an indented line replaces "    " with
 * "  }"). Anything with at most one non-whitespace character was typed.
 */
export const isSingleKeystroke = (text: string): boolean => Array.from(text.trim()).length <= 1

/**
 * Records every document change into the recorder, in order. `assisted` is
 * read per op so the setting can change mid-session without losing the log.
 */
export const recordingExtension = (recorder: Recorder, assisted: () => boolean = () => false) =>
  EditorView.updateListener.of((update) => {
    for (const tr of update.transactions) {
      if (!tr.docChanged) continue
      const trKind = kindForTransaction(tr)
      for (const op of sequentialOps(collectChanges(tr))) {
        const kind =
          trKind ?? (isSingleKeystroke(op.text) ? 'typed' : classifyInsertion(op.text, assisted()))
        recorder.record({ ...op, kind })
      }
    }
  })

export const PASTE_NOTICE = 'Typed by hand only. Paste is off.'

/** Blocks paste and drop. Multi-character input that still arrives becomes a stamp. */
export const noPasteExtension = (onBlocked: () => void) =>
  EditorView.domEventHandlers({
    paste: (event) => {
      event.preventDefault()
      onBlocked()
      return true
    },
    drop: (event) => {
      event.preventDefault()
      onBlocked()
      return true
    },
  })
